import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { AppShell } from '../features/shell/AppShell';
import { useMe } from '../api/queries';
import { qk } from '../api/query-keys';
import { isRyptError, type RyptError } from '../api/errors';
import { behaviourFor, finalMessageFor, messageFor, titleFor } from '../api/error-table';
import { Button } from '../ui/Button';
import { RequestId } from '../ui/RequestId';
import { LiveStatus } from '../ui/LiveStatus';
import { landing } from '../copy/landing';
import { auth0Failed, noTenant, retrying, shell, signInAgain, splash } from '../copy/shell';
import { auth0Line } from './auth0Line';
import { beginReauth, forceReauth, reauthBlocked, signOut } from './auth0Client';
import s from './RequireSession.module.css';

/**
 * Nothing at all for 400 ms.
 *
 * `/v1/me` is usually answered before a spinner could finish its first rotation, and a
 * flash of chrome on every navigation reads as slower than silence does.
 */
function Splash() {
  const [phase, setPhase] = useState<'quiet' | 'opening' | 'slow'>('quiet');

  useEffect(() => {
    const a = setTimeout(() => setPhase('opening'), splash.quietMs);
    const b = setTimeout(() => setPhase('slow'), splash.explainAfterMs);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, []);

  if (phase === 'quiet') return null;

  return (
    <main className={s.centre}>
      <div className={s.splash}>
        <hr className="mastheadRule" />
        <p className={s.wordmark}>{landing.wordmark}</p>
        {/* The splash itself appears after its quiet spell, so its line is given after
            mount too: arriving in the same paint as its region, it might not be read. */}
        <p className={s.body}>
          <LiveStatus text={splash.opening} />
        </p>
        {/* The first call of a session also wakes rypt's KMS client, which is the one
            honest explanation for a wait this long. Saying so beats a longer spinner. */}
        {phase === 'slow' ? <p className={s.aside}>{splash.stillOpening}</p> : null}
      </div>
    </main>
  );
}

/**
 * Each card is the page, so it is the page's main landmark, and its title is the tab's.
 * Every card title is a static string, never a name the reader typed.
 */
function Card({
  title,
  children,
  actions,
  requestId,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  requestId?: string;
}) {
  return (
    <main className={s.centre}>
      <title>{shell.documentTitle(title)}</title>
      <div className={s.card}>
        <hr className="mastheadRule" />
        <h1 className={s.title}>{title}</h1>
        <div className={s.body}>{children}</div>
        {requestId ? (
          <div className={s.requestId}>
            <RequestId id={requestId} />
          </div>
        ) : null}
        {actions ? <div className={s.actions}>{actions}</div> : null}
      </div>
    </main>
  );
}

/**
 * 503, or no answer at all.
 *
 * There is no sign-out button here by design: `unavailable` means rypt could not check
 * the token, not that the token is bad, and signing out would cost the user their session
 * to fix a problem they do not have.
 *
 * Each cause gets its own words. A request that got no answer says nothing about rypt's
 * identity provider, so a transport failure takes the error table's title, and a line of
 * its own: the table's says nothing was sent twice, false beside a countdown to the next
 * send.
 */
function RetryingCard({
  error,
  attempt,
  exhausted,
  inFlight,
  onRetry,
}: {
  error: RyptError;
  /** The failures so far: the query's failureCount. */
  attempt: number;
  /** The read has failed for good. A count alone cannot say so: it reaches its last value
   *  while the final attempt is still waiting out its backoff. */
  exhausted: boolean;
  /** The read this card's Retry now sent is out, and nothing has answered it for good yet. */
  inFlight: boolean;
  onRetry: () => void;
}) {
  const words =
    error.kind === 'transport'
      ? { title: titleFor(error), cause: retrying.transportCause, spent: retrying.transportExhausted(attempt) }
      : { title: retrying.title, cause: retrying.cause, spent: retrying.exhausted(attempt) };

  return (
    <Card
      title={words.title}
      requestId={error.requestId}
      actions={
        <Button variant="primary" onClick={onRetry}>
          {retrying.retryNow}
        </Button>
      }
    >
      {/* The live element holds the sentence, which changes with the cause, when the
          attempts run out, and to "Retrying now." while a Retry now is out: the card stays
          through that read, so its answer is announced as the line changes back. The
          countdown beside it ticks every second and is outside it: readable, and not
          announced on every tick. */}
      <p>
        <span role="status">{inFlight ? retrying.now : exhausted ? words.spent : words.cause}</span>
        {exhausted || inFlight ? null : (
          <>
            {' '}
            {/* Mounted afresh for each failure, so each wait counts down from its own start:
                the card itself stays through a Retry now, and the read it sends starts
                again from its first failure. */}
            <Countdown key={attempt} attempt={attempt} />
          </>
        )}
      </p>
    </Card>
  );
}

function Countdown({ attempt }: { attempt: number }) {
  const [seconds, setSeconds] = useState(() => estimateBackoffSeconds(attempt));

  useEffect(() => {
    const t = setInterval(() => setSeconds((n) => (n > 0 ? n - 1 : 0)), 1_000);
    return () => clearInterval(t);
  }, []);

  return <span>{retrying.countdown(seconds)}</span>;
}

/**
 * The longest wait queryClient's blind backoff can draw after this many failures, to count
 * down against. TanStack calls retryDelay with the count from before the failure, so the
 * wait after the nth is blindBackoff(n − 1): between half of this and all of it. rypt
 * sends no Retry-After, so an exact number does not exist to be shown.
 */
export function estimateBackoffSeconds(attempt: number): number {
  return Math.min(2 ** Math.max(0, attempt - 1), 20);
}

function SignInAgainCard({ error }: { error?: RyptError }) {
  return (
    <Card
      title={signInAgain.title}
      {...(error?.requestId ? { requestId: error.requestId } : {})}
      actions={
        <Button variant="primary" onClick={forceReauth}>
          {signInAgain.action}
        </Button>
      }
    >
      <p>{signInAgain.line}</p>
    </Card>
  );
}

/**
 * The account is fine and has no membership. "Sign in again" is never offered: it would
 * return the same account with the same missing tenant, forever.
 */
function NoTenantPage({ error }: { error: RyptError }) {
  return (
    <Card
      title={noTenant.title}
      requestId={error.requestId}
      actions={
        <Button variant="secondary" onClick={() => void signOut()}>
          {noTenant.action}
        </Button>
      }
    >
      <p>{messageFor(error)}</p>
    </Card>
  );
}

/**
 * An opaque or wrong-audience token: a build misconfiguration, not a session problem.
 * There is no action, because no click by this user can fix it.
 *
 * Also TenantGate's page for any other answer /v1/me gives, the 403s other than no_tenant,
 * a 429 and the 500. rypt answered those, so the card prints the request id, which is the
 * one thing the reader can usefully pass on. A config error never reached rypt, and its
 * id names no request.
 *
 * The read has stopped retrying by the time this card shows, so it prints the code's final
 * line. A code the retry policy retries, the 500 or rate_limited, can answer differently a
 * moment later and gets a Retry. A code it never retries would be refused the same way
 * again, so it gets no action.
 *
 * The line is a live region, kept mounted through a Retry: it reads "Retrying now." while
 * the read is out, and the answer is announced as it changes back.
 */
function ConfigErrorPage({
  error,
  inFlight,
  onRetry,
}: {
  error: RyptError;
  /** The read this card's Retry sent is out, and nothing has answered it for good yet. */
  inFlight: boolean;
  onRetry: () => void;
}) {
  return (
    <Card
      title={titleFor(error)}
      {...(error.kind === 'api' ? { requestId: error.requestId } : {})}
      actions={
        behaviourFor(error).retry === 'never' ? null : (
          <Button variant="primary" onClick={onRetry}>
            {shell.retry}
          </Button>
        )
      }
    >
      <p role="status">{inFlight ? retrying.now : finalMessageFor(error)}</p>
    </Card>
  );
}

/** Auth0 stopped the sign-in before rypt saw a token. Nothing was refused twice, so this
 *  is not the breaker's card. */
function AuthErrorCard({ error }: { error: Error }) {
  return (
    <Card
      title={auth0Failed.title}
      actions={
        <Button variant="primary" onClick={forceReauth}>
          {auth0Failed.action}
        </Button>
      }
    >
      <p>{auth0Line(error)}</p>
    </Card>
  );
}

/** A card with a Retry, as TenantGate drew it, so the same card can be drawn again. */
type RetryCard =
  | { card: 'retrying'; error: RyptError; attempt: number; exhausted: boolean }
  | { card: 'config'; error: RyptError };

function TenantGate() {
  const me = useMe();
  const queryClient = useQueryClient();
  const [held, setHeld] = useState<{ press: number; card: RetryCard } | null>(null);
  const presses = useRef(0);

  // A refetch alone joins the attempt in flight, and one sleeping out its backoff goes on
  // sleeping. Cancelling first puts the read back where it started, so the refetch sends
  // a request now. With nothing in flight, the cancel does nothing.
  //
  // Either way the read is pending again with no failure, which alone would draw the
  // splash where the card was, silent for its first 400 ms: the pressed button would go,
  // focus would fall to <body>, and the answer would arrive in a new card, unannounced.
  // So the card whose Retry was pressed is held until the read that press sent settles.
  // A second press cancels the first one's read, and that read settling must not drop the
  // hold the second press set, so each release checks it is still its own.
  const retryFrom = (card: RetryCard) => () => {
    const press = ++presses.current;
    setHeld({ press, card });
    void queryClient
      .cancelQueries({ queryKey: qk.me() })
      .then(() => me.refetch())
      .finally(() => setHeld((h) => (h?.press === press ? null : h)));
  };

  const draw = (card: RetryCard, inFlight: boolean) =>
    card.card === 'retrying' ? (
      <RetryingCard
        error={card.error}
        attempt={card.attempt}
        exhausted={card.exhausted}
        inFlight={inFlight}
        onRetry={retryFrom(card)}
      />
    ) : (
      <ConfigErrorPage error={card.error} inFlight={inFlight} onRetry={retryFrom(card)} />
    );

  // While a query is retrying it is still "pending", so the retry card has to read
  // failureReason — waiting for the final error would show a splash for the whole backoff
  // and then jump straight to the exhausted copy, never showing the countdown at all.
  const pending = isRyptError(me.failureReason) ? me.failureReason : null;
  if (me.isPending) {
    if (pending && (pending.code === 'unavailable' || pending.kind === 'transport')) {
      return draw({ card: 'retrying', error: pending, attempt: me.failureCount, exhausted: false }, false);
    }
    // The read a Retry sent, before it has a card of its own: its first attempt, or the
    // policy's retry of a 500 or a 429. The same card at the same place, so React keeps it
    // and the focused button in it. Sending, it says so; paused offline, nothing is sent,
    // and it keeps saying what it said.
    if (held) return draw(held.card, me.fetchStatus === 'fetching');
    return <Splash />;
  }

  const e = isRyptError(me.error) ? me.error : null;
  if (e?.status === 401) {
    // The global onError has already called beginReauth(), so unless the breaker stopped
    // it a redirect is in flight and this render is the last one before it lands. That is
    // as true over an identity already read: every page's calls carry the refused token.
    return reauthBlocked() ? <SignInAgainCard error={e} /> : <Splash />;
  }
  // A membership removed mid-session is as final over data as on a first read: every call
  // the pages make now answers no_tenant, and the card is what offers the way out.
  if (e?.code === 'no_tenant') return <NoTenantPage error={e} />;
  // Data first. A refresh that fails over an identity already read keeps the app, an open
  // dialog included, and each page reports its own failed reads. Only a first read that
  // failed leaves no app to show.
  if (e && me.data === undefined) {
    if (e.kind === 'config') return draw({ card: 'config', error: e }, false);
    if (e.code === 'unavailable' || e.kind === 'transport') {
      return draw({ card: 'retrying', error: e, attempt: me.failureCount, exhausted: true }, false);
    }
    return draw({ card: 'config', error: e }, false);
  }

  return <AppShell />;
}

export function RequireSession() {
  const { isLoading, isAuthenticated, error } = useAuth0();
  const location = useLocation();

  useEffect(() => {
    if (isLoading || isAuthenticated || error) return;
    // A reload or a new tab loses the in-memory refresh token, so this is the normal path
    // back in, not an error. The breaker decides whether it is the second one in a row.
    beginReauth(`${location.pathname}${location.search}${location.hash}`);
  }, [isLoading, isAuthenticated, error, location]);

  if (isLoading) return <Splash />;
  if (error) return <AuthErrorCard error={error} />;
  if (!isAuthenticated) return reauthBlocked() ? <SignInAgainCard /> : <Splash />;
  return <TenantGate />;
}

export default RequireSession;
